import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FlowLayout } from '@/ds'
import { useAppNavigate } from '@/lib/query-params'
import { checkinService, fieldRules, stepErrors } from '@/services/checkin'
import type { GuestField } from '@/services/checkin'
import { holderTraveler, useCheckinFlow } from '../CheckinFlow'
import { backLabelKey, neighbours, progressFor, stepPath } from '../flow'
import { errorsAfterGuestChange, GuestFields } from '../fields'
import type { FieldErrors } from '../fields'
import { useApiLang, useSave } from '../useSave'

/** El país se elige junto al documento, antes de aplicar sus reglas de validación. */
export const LEGAL_FIELDS: GuestField[] = ['address', 'postalCode', 'city', 'provinceId', 'municipalityId', 'sexId', 'relationship']
/** Todo lo que se valida antes de guardar en E5, por si el borrador se retomó a medias. */
const ALL_FIELDS: GuestField[] = ['name', 'surname1', 'surname2', 'email', 'identifierTypeId', 'identifier', 'supportNumber', 'bornDate', ...LEGAL_FIELDS]
const STEP_OF: Partial<Record<GuestField, '2' | '3b'>> = { name: '2', surname1: '2', surname2: '2', email: '2', identifierTypeId: '3b', identifier: '3b', supportNumber: '3b', bornDate: '3b' }

/**
 * Paso 5 · Datos que pide la ley (CheckIn.dc.html step=5). Provincia y municipio en cascada (E8, D24);
 * sin "Cómo pagas la estancia" (D24). Al continuar se guarda el registro en el PMS (E5) con las fotos.
 */
export function Ley() {
  const { t } = useTranslation()
  const { go } = useAppNavigate()
  const lang = useApiLang()
  const { stay, draft, updateGuest, update, volatile } = useCheckinFlow()
  const [errors, setErrors] = useState<FieldErrors>({})
  const { saving, error, run } = useSave()
  const { prev, next } = neighbours('5', stay, draft.persona, draft.method, draft.entry)
  const holder = holderTraveler(stay)
  // El parentesco solo se enseña a acompañantes (desactivado con motivo si son adultos) o si la persona es menor.
  const fields = draft.persona === 'companion' || fieldRules(stay, draft.guest).relationship.visible ? LEGAL_FIELDS : LEGAL_FIELDS.filter((f) => f !== 'relationship')
  const onGuestChange = (patch: Partial<typeof draft.guest>) => {
    updateGuest(patch)
    setErrors((current) => errorsAfterGuestChange(current, patch, stay, draft.guest))
  }

  const submit = async () => {
    const e = stepErrors(ALL_FIELDS, stay, draft.guest)
    setErrors(e)
    const bad = Object.keys(e) as GuestField[]
    // Con captura no hay paso 2 (D33): nombre, apellidos y email se corrigen en la revisión.
    const elsewhere = bad.map((f) => (draft.entry === 'capture' && STEP_OF[f] === '2' ? '3b' : STEP_OF[f])).find(Boolean)
    if (elsewhere && !bad.some((f) => LEGAL_FIELDS.includes(f))) return go(stepPath(elsewhere))
    if (bad.length > 0) return
    // Escaneado pero sin imágenes (se retomó el borrador tras recargar): hay que repetir la foto.
    if (draft.method === 'scan' && !volatile.attachments.front && !volatile.attachments.back) return go(stepPath('3'))
    const saved = await run(() => checkinService.saveGuest(stay, draft.guest, volatile.attachments, volatile.rawData, lang))
    if (!saved) return
    update({ travelerId: saved.travelerId, documents: saved.documents })
    go(stepPath(next))
  }

  return (
    <FlowLayout
      progress={{ ...progressFor('5', stay, draft.persona, draft.method, draft.entry), backLabel: t(backLabelKey(prev)), onBack: () => go(stepPath(prev)) }}
      title={t('v2.checkin.ley.titulo')}
      intro={t('v2.checkin.ley.intro')}
      primary={{ label: t('v2.checkin.continuar'), onClick: () => void submit(), loading: saving }}
      footerMessage={error}
    >
      <GuestFields
        stay={stay}
        guest={draft.guest}
        fields={fields}
        errors={errors}
        onChange={onGuestChange}
        onBlurField={(f) => setErrors((p) => ({ ...p, [f]: stepErrors([f], stay, draft.guest)[f] }))}
        holderName={draft.persona === 'companion' ? (holder?.name ?? null) : null}
      />
      <p className="checkin__note">{t('v2.checkin.ley.nota')}</p>
    </FlowLayout>
  )
}
